import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CrudService, HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { FormDataGridParameters, FormDataResponse, FormIOData } from "../dto/form-data";
import { FormioHTMLData } from "@main/formio/components/formio-pdf/formio-pdf-generator.component";
import { FileDetails } from "@main/file-storage";
import { SetUTCTimeZone } from "@bbwt/utils";
import { EntityId } from "@bbwt/interfaces";
import { FormFieldDataUpdate } from "../components/bb-form-version-handler/bb-form-version-handler.models";
import { HistoricalItem } from "@mipro/classes/historical-item";

@Injectable()
export class FormIODataService extends CrudService<FormIOData> {
    public readonly url = "api/formio-data";
    public readonly entityTitle = "Form";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }

    async sendFormDataJson(formData: FormIOData): Promise<FormDataResponse> {
        return await this.handleRequest<FormDataResponse>(
            this.http.post<FormDataResponse>(`${this.url}/form-data`, formData),
            this.handlersFactory.getDefault());
    }

    async createPdfFormData(formDataID: EntityId, data: FormioHTMLData, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<FileDetails> {
        return await this.handleRequest<FileDetails>(
            this.http.post<FileDetails>(`${this.url}/form-data-create-pdf/${formDataID}`, data),
            this.handlersFactory.getDefault(responseHandlerSettings)
        );
    }

    async markAsComplete(formDataId: string) {
        return await this.handleRequest<FileDetails>(
            this.http.post<FileDetails>(`${this.url}/mark-as-complete/${formDataId}`, null),
            this.handlersFactory.getDefault()
        );
    }

    async updateGridParameters(id: number | string, gridParams: FormDataGridParameters): Promise<boolean> {
        gridParams = {
            consultationHistoryGridFromDate: SetUTCTimeZone(gridParams.consultationHistoryGridFromDate),
            consultationHistoryGridToDate: SetUTCTimeZone(gridParams.consultationHistoryGridToDate),
            episodeNotesGridEpisodeId: gridParams.episodeNotesGridEpisodeId,
            episodeNotesGridFromDate: SetUTCTimeZone(gridParams.episodeNotesGridFromDate),
            episodeNotesGridToDate: SetUTCTimeZone(gridParams.episodeNotesGridToDate),
            medicalHistoryGridFromDate: SetUTCTimeZone(gridParams.medicalHistoryGridFromDate),
            medicalHistoryGridToDate: SetUTCTimeZone(gridParams.medicalHistoryGridToDate),
            selectedEpisodesJson: gridParams.selectedEpisodesJson
        }

        return await this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/grid-params/${id}`, gridParams),
            this.handlersFactory.getDefault());
    }

    async updateEpisodeFormData(id: number | string, episodeId: number | string): Promise<any> {
        return await this.handleRequest<any>(
            this.http.post(`${this.url}/form-data-update-episode/${id}/${episodeId}`, null),
            this.handlersFactory.getForUpdate(this.entityTitle));
    }

    async getFormDataJson(id: string): Promise<string> {
        return await this.handleRequest<string>(
            this.http.post<string>(`${this.url}/form-data/${id}`, null),
            this.handlersFactory.getDefault());
    }

    async checkIfFormHasData(formDefinitionId: EntityId): Promise<boolean> {
        return await this.handleRequest<boolean>(
            this.http.get<boolean>(`${this.url}/has-data/${formDefinitionId}`),
            this.handlersFactory.getDefault()
        )
    }

    async updateFormData(formDefinitionId: EntityId, updates: FormFieldDataUpdate[]): Promise<void> {
        return await this.httpPost<void>(`update-form-data/${formDefinitionId}`, updates);
    }

    getHistoricalItems(formDataId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical-items/${formDataId}`),
            this.handlersFactory.getForReadAll());
    }
}
